import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus } from 'lucide-react';
import { useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { toast } from 'sonner';

import { api, unwrap } from '@/api/client';
import { mensajeDe } from '@/api/errors';
import { PermissionGate } from '@/auth/PermissionGate';
import { useCan } from '@/auth/queries';
import { Campo } from '@/components/Campo';
import { Cargando, Encabezado, ErrorCarga, Vacio } from '@/components/Estados';
import { Modal } from '@/components/Modal';
import { Paginador } from '@/components/Paginador';
import { useFiltrosUrl } from '@/hooks/useFiltrosUrl';

import { SelectorEspecialidad } from '../comun/SelectorEspecialidad';
import { useClases, type Clase, type ClaseItem } from './queries';

const CLAVES = ['q', 'specialtyId', 'workshop', 'published', 'noTopic'] as const;

/** Columnas de conteo en el orden de las pestañas que ve el alumno. */
const CONTEOS: [keyof ClaseItem['counts'], string, string][] = [
  ['case', 'C', 'Caso clínico'],
  ['videos', 'MC', 'Master Class (videos)'],
  ['ultra', 'UR', 'Ultra Resumen (páginas)'],
  ['gpcSummaries', 'RG', 'Resúmenes GPC'],
  ['english', 'IM', 'Inglés Médico'],
  ['guides', 'GG', 'Guías GPC'],
  ['plus', 'EP', 'ENARM Plus'],
  ['presentations', 'P', 'Presentación'],
  ['quiz', 'Q', 'Quiz'],
  ['topics', 'T', 'Temas del temario'],
];

function NuevaClase({ onCerrar }: { onCerrar: () => void }) {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [f, setF] = useState({ name: '', specialtyId: null as number | null, isWorkshop: false });
  const crear = useMutation({
    mutationFn: () =>
      unwrap<Clase>(
        api.POST('/api/v1/admin/classes', {
          body: { name: f.name.trim(), specialtyId: f.specialtyId ?? 0, isWorkshop: f.isWorkshop },
        }),
      ),
    onSuccess: (d) => {
      void qc.invalidateQueries({ queryKey: ['contenido'] });
      toast.success('Clase creada. No la ve ningún alumno hasta publicarla.');
      void navigate(`/contenido/clases/${d.id}`);
    },
    onError: (e) => toast.error(mensajeDe(e)),
  });
  return (
    <Modal abierto onCerrar={onCerrar} titulo="Nueva clase">
      <form
        className="space-y-4"
        onSubmit={(e) => {
          e.preventDefault();
          if (f.specialtyId === null) toast.error('Elige la especialidad.');
          else crear.mutate();
        }}
      >
        <Campo etiqueta="Nombre" ayuda="Hasta 120 caracteres.">
          <input
            className="input-vidrio"
            required
            minLength={2}
            maxLength={120}
            autoFocus
            value={f.name}
            onChange={(e) => setF({ ...f, name: e.target.value })}
          />
        </Campo>
        <Campo etiqueta="Especialidad *">
          <SelectorEspecialidad
            valor={f.specialtyId}
            requerido
            vacio="Elige una especialidad"
            onCambio={(specialtyId) => setF({ ...f, specialtyId })}
          />
        </Campo>
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            className="h-4 w-4 accent-[var(--color-azul-600)]"
            checked={f.isWorkshop}
            onChange={(e) => setF({ ...f, isWorkshop: e.target.checked })}
          />
          Es taller
        </label>
        <div className="flex justify-end gap-2">
          <button type="button" className="btn-cs btn-contorno" onClick={onCerrar}>
            Cancelar
          </button>
          <button type="submit" className="btn-cs btn-primario" disabled={crear.isPending}>
            Crear y abrir
          </button>
        </div>
      </form>
    </Modal>
  );
}

function Lista() {
  const { filtros, cambiar, query } = useFiltrosUrl(CLAVES);
  const editable = useCan('contenido', 'editar');
  const q = useClases({ ...query, page: filtros.page, pageSize: 50 });
  const [nueva, setNueva] = useState(false);
  const sel = (k: (typeof CLAVES)[number], etiqueta: string, opciones: [string, string][]) => (
    <Campo etiqueta={etiqueta}>
      <select
        className="input-vidrio"
        value={filtros[k]}
        onChange={(e) => cambiar({ [k]: e.target.value })}
      >
        {opciones.map(([v, t]) => (
          <option key={v} value={v}>
            {t}
          </option>
        ))}
      </select>
    </Campo>
  );
  return (
    <>
      <Encabezado
        titulo="Clases"
        descripcion="El alumno ve una clase solo si está visible y un tema del temario apunta a ella (los talleres no necesitan tema)."
        acciones={
          editable && (
            <button type="button" className="btn-cs btn-primario" onClick={() => setNueva(true)}>
              <Plus size={16} aria-hidden /> Nueva clase
            </button>
          )
        }
      />
      <div className="panel-vidrio mb-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
        <Campo etiqueta="Buscar">
          <input
            className="input-vidrio"
            defaultValue={filtros.q}
            placeholder="Nombre, ID o código"
            onKeyDown={(e) => e.key === 'Enter' && cambiar({ q: e.currentTarget.value })}
            onBlur={(e) => e.target.value !== filtros.q && cambiar({ q: e.target.value })}
          />
        </Campo>
        <Campo etiqueta="Especialidad">
          <SelectorEspecialidad
            valor={filtros.specialtyId ? Number(filtros.specialtyId) : null}
            vacio="Todas"
            onCambio={(v) => cambiar({ specialtyId: v ? String(v) : '' })}
          />
        </Campo>
        {sel('workshop', 'Tipo', [
          ['', 'Todas'],
          ['false', 'Clases'],
          ['true', 'Talleres'],
        ])}
        {sel('published', 'Visibilidad', [
          ['', 'Todas'],
          ['true', 'Visibles'],
          ['false', 'Ocultas'],
        ])}
        {sel('noTopic', 'Temario', [
          ['', 'Todas'],
          ['true', 'Sin tema en el temario'],
        ])}
      </div>
      {q.isPending && <Cargando />}
      {q.isError && <ErrorCarga error={q.error} onReintentar={() => void q.refetch()} />}
      {q.data?.data.length === 0 && <Vacio titulo="Sin clases con esos filtros" />}
      {q.data && q.data.data.length > 0 && (
        <div className="panel-vidrio overflow-x-auto p-0">
          <table className="tabla-cs w-full text-sm">
            <thead>
              <tr>
                <th>ID</th>
                <th>Clase</th>
                <th>Especialidad</th>
                <th>Estado</th>
                {CONTEOS.map(([k, corto, largo]) => (
                  <th key={k} title={largo} className="text-center">
                    {corto}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {q.data.data.map((c) => (
                <tr key={c.id}>
                  <td className="tabular">{c.id}</td>
                  <td className="max-w-sm">
                    <Link
                      to={`/contenido/clases/${c.id}`}
                      className="font-semibold hover:underline"
                    >
                      {c.name}
                    </Link>
                    {c.isWorkshop && <span className="insignia ml-2">Taller</span>}
                  </td>
                  <td>{c.specialtyName ?? '—'}</td>
                  <td>
                    {c.isPublished ? (
                      <span className="insignia insignia-activa">Visible</span>
                    ) : (
                      <span className="insignia">Oculta</span>
                    )}
                  </td>
                  {CONTEOS.map(([k, , largo]) => (
                    <td
                      key={k}
                      className={`tabular text-center ${c.counts[k] === 0 ? 'text-texto-3' : 'font-semibold'}`}
                      title={`${largo}: ${c.counts[k]}`}
                    >
                      {c.counts[k] === 0 ? '·' : c.counts[k]}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {q.data && (
        <Paginador
          page={q.data.page}
          pageSize={q.data.pageSize}
          total={q.data.total}
          onPage={(page) => cambiar({ page })}
        />
      )}
      {nueva && <NuevaClase onCerrar={() => setNueva(false)} />}
    </>
  );
}

export function ClasesPage() {
  return (
    <PermissionGate seccion="contenido">
      <Lista />
    </PermissionGate>
  );
}
